import type { LineSeriesType } from '@mui/x-charts-pro';
import type { GetFormCompletionByTypeResponse } from '#services/api/insights.ts';

import { Box, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ChartDataProvider,
  ChartsAxisHighlight,
  ChartsLegend,
  ChartsSurface,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  LinePlot,
  MarkPlot,
} from '@mui/x-charts-pro';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';

import { SeeMore } from '#components/SeeMore/SeeMore.tsx';
import { useSeeMore } from '#components/SeeMore/use-see-more.ts';
import { formCompletionByType } from '#lib/react-query/queries.ts';
import {
  InfoEmphasis,
  InfoList,
  InfoParagraph,
  InfoSection,
  InfoTemplate,
} from '#routes/Home/Widgets/InfoTemplateComponents.tsx';
import {
  CHART_AXIS_STYLES,
  CHART_SERIES_COLORS,
  CHART_X_AXIS_CONFIG,
  WIDGET_CHART_HEIGHT,
} from '#utils/charts.ts';
import { FORMATS } from '#utils/dates.ts';
import { Base } from './Base';
import { EmptyDataOverlay } from './EmptyDataOverlay.tsx';
import { useFormWidget } from './use-form-widget.ts';

const widgetTitle = 'Form Completion By Type';

const infoTemplate = (
  <InfoTemplate>
    <InfoSection title="What it is &amp; Problems it Solves">
      <InfoParagraph>
        Displays trends in the&nbsp;
        <InfoEmphasis>number of forms completed</InfoEmphasis>, broken down by
        form type, with daily data points. This helps organizations identify
        which forms are most or least frequently completed over time and spot
        changes in patient engagement with specific forms.
      </InfoParagraph>
    </InfoSection>

    <InfoSection title="How it Works (Calculation)" sx={{ mb: 2 }}>
      <InfoSection title="Count of:" indented>
        <InfoList items={['Forms completed within the selected date range']} />
      </InfoSection>

      <InfoSection title="How it’s displayed:" indented>
        <InfoList
          items={[
            'Each form type is shown as its own trendline on a time series chart',
            'Values are whole numbers (counts), not percentages',
          ]}
        />
      </InfoSection>

      <InfoSection title="Includes" indented>
        <InfoList
          items={[
            'Forms with a valid completed timestamp',
            'Forms matching applied date and other dashboard filters',
            'Deleted forms',
          ]}
        />
      </InfoSection>

      <InfoSection title="Excludes" indented>
        <InfoList
          items={[
            'Forms missing a completed timestamp for the selected time range',
          ]}
        />
      </InfoSection>

      <InfoSection title="Notes &amp; Caveats" indented>
        <InfoList
          items={[
            <>
              Tracks <InfoEmphasis>completed forms only</InfoEmphasis>
              —does not count assigned-but-incomplete forms or canceled forms
            </>,
            <>
              Date range is based on{' '}
              <InfoEmphasis>completion date</InfoEmphasis>, not assignment date
            </>,
            'Data updates nightly, so very recent changes may not yet appear.',
          ]}
        />
      </InfoSection>
    </InfoSection>
  </InfoTemplate>
);

function select(rawResponse: GetFormCompletionByTypeResponse) {
  const response = {
    ...rawResponse,
    data: rawResponse.data
      .map((item) => ({ ...item, period: new Date(item.period) }))
      .sort((a, b) => a.title.localeCompare(b.title)),
  };

  // We need to transform the data to a format that can be used by the chart
  const seriesDataKeys = [
    ...new Set(response.data.map(({ id, title }) => `${id}|||${title}`)),
  ];
  const baseSeries = Object.fromEntries(seriesDataKeys.map((key) => [key, 0]));
  const seriesObject = response.data.reduce<
    Record<string, { [dataKey: string]: number | Date; period: Date }>
  >((acc, cur) => {
    const periodKey = format(cur.period, FORMATS.MYSQL_DATETIME);
    const dataKey = `${cur.id}|||${cur.title}`;
    if (!acc[periodKey]) {
      acc[periodKey] = { ...baseSeries, period: cur.period };
    }
    acc[periodKey][dataKey] = cur.count;
    return acc;
  }, {});
  const dataset = Object.values(seriesObject);
  const series: LineSeriesType[] = seriesDataKeys.map((key) => ({
    dataKey: key,
    label: key.split('|||')[1],
    labelMarkType: 'circle',
    type: 'line',
  }));

  return { ...response, dataset, series };
}

export function FormCompletionByType() {
  const widget = useFormWidget('form-completion-by-type');

  const theme = useTheme();

  const {
    minCreated: minCompletedDate,
    maxCreated: maxCompletedDate,
    ...params
  } = widget.getCommonRequestParams();

  // Fetch form trends data
  const { data, isLoading } = useQuery({
    ...formCompletionByType({
      ...params,
      minCompletedDate,
      maxCompletedDate,
    }),
    select,
  });

  const seeMore = useSeeMore({ data: data?.series });

  if (isLoading) {
    return (
      <Base title={widgetTitle}>
        <Skeleton variant="rounded" height={WIDGET_CHART_HEIGHT} />
      </Base>
    );
  }

  return (
    <Base
      id="form-completion-by-type"
      title={widgetTitle}
      size="full"
      infoTemplate={infoTemplate}
      drilldownRoutePath={widget.getDrilldownRoutePath()}
    >
      {(isOpenedInDialog) => (
        <Box height="100%">
          {data && data.data.length > 0 ? (
            <ChartDataProvider
              colors={(mode) => CHART_SERIES_COLORS[mode]}
              dataset={data.dataset}
              height={isOpenedInDialog ? undefined : WIDGET_CHART_HEIGHT}
              // Need to set right marin to fit x-axis tick label
              margin={{ right: 30 }}
              series={seeMore.data}
              xAxis={[
                {
                  dataKey: 'period',
                  height: 60,
                  label: 'Dates',
                  scaleType: 'time',
                  ...CHART_X_AXIS_CONFIG[data.groupedBy],
                },
              ]}
              yAxis={[
                {
                  label: 'Number of Completions',
                },
              ]}
            >
              <ChartsLegend
                sx={{
                  justifyContent: 'center',
                  '.MuiChartsLegend-label': {
                    color: theme.palette.muted[4],
                  },
                }}
              />
              {seeMore.canSeeMore && (
                <SeeMore
                  flag={seeMore.flag}
                  additional={seeMore.additional}
                  toggle={seeMore.toggle}
                />
              )}
              <ChartsSurface>
                <ChartsXAxis sx={CHART_AXIS_STYLES(theme)} />
                <ChartsYAxis sx={CHART_AXIS_STYLES(theme)} />
                <LinePlot />
                <MarkPlot />
                <ChartsAxisHighlight x="line" y="none" />
                <ChartsTooltip />
              </ChartsSurface>
            </ChartDataProvider>
          ) : (
            <EmptyDataOverlay variant={isOpenedInDialog ? 'dialog' : 'chart'} />
          )}
        </Box>
      )}
    </Base>
  );
}
